"use client";

import type { FC } from "react";
import { useLayoutEffect, useRef, useState } from "react";
import { cn } from "utils";
import {
  buildProgressLinePath,
  clampBracketLabelCenterX,
  computeLineChartPlotWidth,
  lastPoint,
  LINE_CHART_DEFAULT_PLOT_WIDTH,
  LINE_CHART_LABEL_OFFSET_X,
  LINE_CHART_PLOT_PADDING_X,
  pathWidthForLineEndX,
  resolveLineChartEndX,
} from "../buildChartPath";
import { formatYearsSoonerShort } from "../formatDeltaLabel";
import type { LineComparisonChartPayload } from "../types";

const CHART_H = 118;
const CHART_OFFSET_X = LINE_CHART_LABEL_OFFSET_X;
const PLOT_PADDING_X = LINE_CHART_PLOT_PADDING_X;
const PADDING_Y = 0;
const ENDPOINT_DOT_RADIUS = 6.5;
const ENDPOINT_DOT_STROKE = 2.5;
/** Half of dot extends above cy=0 / below plot bottom — keep in viewBox. */
const ENDPOINT_DOT_OUTER_RADIUS = ENDPOINT_DOT_RADIUS + ENDPOINT_DOT_STROKE / 2;
const TARGET_DASHED_Y = 0.75;

interface LineComparisonChartProps {
  data: LineComparisonChartPayload;
}

interface EndpointDotProps {
  cx: number;
  cy: number;
  strokeClassName: string;
  testId: string;
}

const EndpointDot: FC<EndpointDotProps> = ({
  cx,
  cy,
  strokeClassName,
  testId,
}) => (
  <circle
    cx={cx}
    cy={cy}
    r={ENDPOINT_DOT_RADIUS}
    strokeWidth={ENDPOINT_DOT_STROKE}
    className={cn("fill-background_solid_neutral_primary", strokeClassName)}
    data-testid={testId}
  />
);

export const LineComparisonChart: FC<LineComparisonChartProps> = ({ data }) => {
  const plotContainerRef = useRef<HTMLDivElement>(null);
  const [plotWidth, setPlotWidth] = useState(LINE_CHART_DEFAULT_PLOT_WIDTH);

  useLayoutEffect(() => {
    const element = plotContainerRef.current;
    if (!element) {
      return;
    }

    const updatePlotWidth = () => {
      setPlotWidth(computeLineChartPlotWidth(element.clientWidth));
    };

    updatePlotWidth();

    const resizeObserver = new ResizeObserver(updatePlotWidth);
    resizeObserver.observe(element);
    return () => resizeObserver.disconnect();
  }, []);

  const edEndX = resolveLineChartEndX(
    data.onYourOwnYear,
    data.withEdYear,
    plotWidth,
    PLOT_PADDING_X,
  );
  const edChartWidth = pathWidthForLineEndX(edEndX, PLOT_PADDING_X);

  const ownPath = buildProgressLinePath(
    data.onYourOwn,
    plotWidth,
    CHART_H,
    PLOT_PADDING_X,
    PADDING_Y,
  );
  const edPath = buildProgressLinePath(
    data.withEd,
    edChartWidth,
    CHART_H,
    PLOT_PADDING_X,
    PADDING_Y,
  );
  const ownEnd = lastPoint(
    data.onYourOwn,
    plotWidth,
    CHART_H,
    PLOT_PADDING_X,
    PADDING_Y,
  );
  const edEnd = lastPoint(
    data.withEd,
    edChartWidth,
    CHART_H,
    PLOT_PADDING_X,
    PADDING_Y,
  );

  const bracketLeft = edEnd && ownEnd ? Math.min(edEnd.x, ownEnd.x) : 0;
  const bracketWidth = edEnd && ownEnd ? Math.abs(ownEnd.x - edEnd.x) : 0;
  const bracketMidX = edEnd && ownEnd ? (edEnd.x + ownEnd.x) / 2 : 0;
  const labelCenterX = clampBracketLabelCenterX(
    bracketMidX,
    CHART_OFFSET_X,
    plotWidth,
    PLOT_PADDING_X,
  );

  return (
    <div
      className="flex w-full flex-col gap-3.5"
      data-testid="result-line-chart"
    >
      <div
        ref={plotContainerRef}
        className="relative h-[168px] w-full overflow-visible"
        data-testid="result-line-chart-plot"
        data-plot-width={plotWidth}
      >
        <p className="body_medium_s absolute left-0 top-[35px] text-content_secondary">
          {data.targetLabel}
        </p>

        {edEnd && ownEnd && data.deltaYears > 0 && bracketWidth > 0 ? (
          <>
            <div
              className="absolute top-[6px] -translate-x-1/2"
              style={{ left: `${labelCenterX}px` }}
              data-testid="result-chart-bracket-label"
            >
              <p className="body_bold_s whitespace-nowrap text-center text-content_brand_primary">
                {formatYearsSoonerShort(data.deltaYears)}
              </p>
            </div>
            <div
              className="absolute top-[24px]"
              style={{
                left: `${CHART_OFFSET_X + bracketLeft}px`,
                width: `${bracketWidth}px`,
              }}
              data-testid="result-chart-bracket"
            >
              <div className="border-t border-content_brand_primary" />
            </div>
          </>
        ) : null}

        <svg
          className="absolute overflow-visible"
          style={{
            left: `${CHART_OFFSET_X}px`,
            top: `${42 - ENDPOINT_DOT_OUTER_RADIUS}px`,
          }}
          width={plotWidth}
          height={CHART_H + ENDPOINT_DOT_OUTER_RADIUS}
          viewBox={`0 ${-ENDPOINT_DOT_OUTER_RADIUS} ${plotWidth} ${CHART_H + ENDPOINT_DOT_OUTER_RADIUS}`}
          aria-hidden
        >
          <line
            x1={PLOT_PADDING_X}
            y1={TARGET_DASHED_Y}
            x2={plotWidth - PLOT_PADDING_X}
            y2={TARGET_DASHED_Y}
            strokeWidth={1.5}
            strokeDasharray="4 4"
            className="stroke-content_quaternary"
            data-testid="result-chart-target-dashed"
          />
          <path
            d={ownPath}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            className="text-content_tertiary"
          />
          <path
            d={edPath}
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            className="text-content_brand_primary"
          />
          {edEnd ? (
            <EndpointDot
              cx={edEnd.x}
              cy={edEnd.y}
              strokeClassName="stroke-content_brand_primary"
              testId="result-chart-dot-ed"
            />
          ) : null}
          {ownEnd ? (
            <EndpointDot
              cx={ownEnd.x}
              cy={ownEnd.y}
              strokeClassName="stroke-content_tertiary"
              testId="result-chart-dot-own"
            />
          ) : null}
        </svg>
      </div>

      <div
        className="flex w-full items-center justify-between"
        data-testid="result-chart-legend"
      >
        <div className="flex items-center gap-2">
          <span className="size-2 rounded-full bg-content_brand_primary" />
          <p className="body_medium_s text-content_brand_primary">
            {`With Ed · ${data.withEdYear}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="size-2 rounded-full bg-content_quaternary" />
          <p className="body_medium_s text-content_quaternary">
            {`On your own · ${data.onYourOwnYear}`}
          </p>
        </div>
      </div>
    </div>
  );
};

/** Retirement shares the same line-comparison visual as million. */
export const RetirementLineChart = LineComparisonChart;
